import {
    ComponentPropsWithoutRef,
    ReactNode,
    Suspense,
    createContext,
    useContext,
    useMemo,
    useState,
} from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Button, ButtonProps } from '@givebutter/primitives/button';
import { brandColors, getMostReadableHexColor } from '@givebutter/util';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    IAllProps,
    IProps,
    Editor as TinyEditor,
} from '@tinymce/tinymce-react/lib/cjs/main/ts/components/Editor';
import { aiRequestHandler } from 'api/ai';
import { getEngageConfiguration } from 'api/engage';
import { uploadFile, uploadRemoteMedia } from 'api/media';
import { transformEngageTags } from 'assets/js/pages/engage/util';
import { Settings } from 'constants/settings';
import css from 'css/fonts.module.css?inline';
import { CanvaCallbacks, CanvaEditor, useCanva } from 'customHooks/useCanva';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { AnimatePresence, HTMLMotionProps, motion } from 'framer-motion';
import { isImage, isZip } from 'helpers/regex';
import { accountQueryKeys } from 'queries/account-query-keys';
import { Bookmark } from 'tinymce';
import { GivebutterAxiosError } from 'types/error';

class TinyMceUploadError extends Error {
    remove: boolean = false;
    constructor(message: string, remove?: boolean) {
        super(message);
        this.name = 'TinyMceUploadError';
        if (typeof remove !== 'undefined') {
            this.remove = remove;
        }
    }
}

const WysiwygConstants = {
    TINY_TOOLBAR_ID: 'gb-tiny-toolbar',
    MINI_EDITOR_PLUGINS: [
        'autolink',
        'lists',
        'link',
        'image',
        'editimage',
        'searchreplace',
        'visualblocks',
        'insertdatetime',
        'help',
        'wordcount',
        'powerpaste',
        'emoticons',
        'media',
        'code',
    ],
    DESCRIPTION_EDITOR_PLUGINS: ['autolink', 'lists', 'link', 'help', 'powerpaste', 'code'],
    MINI_EDITOR_TOOLBAR:
        'undo redo blocks bold italic underline strikethrough forecolor link mergetags canva image media emoticons numlist bullist hr',
    DESCRIPTION_EDITOR_TOOLBAR: 'undo redo bold italic forecolor link numlist bullist',
    APP_FONT: `
        ${css}
        body {
            font-family: Matter;
            -webkit-font-variant-ligatures: no-contextual;
            font-variant-ligatures: no-contextual;
        }
    `,
};

interface IWysiwygContext {
    editorInstance?: TinyEditor['editor'];
    setEditorInstance: (editor: TinyEditor['editor']) => void;
    lastBookmark?: Bookmark;
    setLastBookmark: (bookmark: Bookmark) => void;
}

const WysiwygContext = createContext<IWysiwygContext>({
    editorInstance: undefined,
    setEditorInstance: () => {},
    lastBookmark: undefined,
    setLastBookmark: () => {},
});

interface WysiwygProviderProps {
    children: ReactNode;
}
const WysiwygProvider = (props: WysiwygProviderProps) => {
    const { children } = props;
    const [editorInstance, setEditorInstance] = useState<TinyEditor['editor']>();
    const [lastBookmark, setLastBookmark] = useState<Bookmark | undefined>();

    const value = useMemo(
        () => ({
            editorInstance,
            setEditorInstance,
            lastBookmark,
            setLastBookmark,
        }),
        [editorInstance, lastBookmark],
    );

    return (
        <WysiwygContext.Provider value={value}>
            <Suspense fallback={null}>{children}</Suspense>
        </WysiwygContext.Provider>
    );
};

interface WysiwygContentContainerProps extends ComponentPropsWithoutRef<'div'> {}
const WysiwygContentContainer = (props: WysiwygContentContainerProps) => {
    const { className = '', ...rest } = props;

    return (
        <div
            className={`relative min-h-screen rounded-sm bg-[#d1d1d6] p-2 ${className}`}
            {...rest}
        />
    );
};

interface WysiwygMarginProps extends Omit<ButtonProps, 'variant'> {}
const WysiwygMargins = (props: WysiwygMarginProps) => {
    const { className = '', ...rest } = props;
    const { lastBookmark, editorInstance } = useContext(WysiwygContext);

    return (
        <Button
            variant="none"
            aria-label="Press to continue editing"
            onClick={e => {
                e.preventDefault();
                if (editorInstance && !editorInstance.hasFocus() && lastBookmark) {
                    editorInstance.focus();
                    editorInstance.selection.moveToBookmark(lastBookmark);
                }
            }}
            className={`cursor-text items-start justify-start !whitespace-pre-wrap bg-white text-left ${className}`}
            {...rest}
        />
    );
};

interface WysiwygToolbarProps extends ComponentPropsWithoutRef<'div'> {}
const WysiwygToolbar = (props: WysiwygToolbarProps) => {
    const { id = WysiwygConstants.TINY_TOOLBAR_ID, className = '', ...rest } = props;

    return <div className={`bg-white ${className}`} id={id} {...rest} />;
};

interface WysiwygCssResetProps extends ComponentPropsWithoutRef<'div'> {}

const WysiwygCssReset = (props: WysiwygCssResetProps) => {
    const { className = '', ...rest } = props;

    return <div className={`prose prose-base prose-gray flex ${className}`} {...rest} />;
};

interface WysiwygCursorManagerProps extends Omit<ButtonProps, 'variant'> {}
const WysiwygCursorManager = (props: WysiwygCursorManagerProps) => {
    const { onClick, className = '', ...rest } = props;
    const { editorInstance } = useContext(WysiwygContext);

    return (
        <Button
            variant="none"
            aria-label="Move cursor to the end of the editor"
            tabIndex={-1}
            onClick={e => {
                if (editorInstance) {
                    editorInstance.selection.select(editorInstance.getBody(), true);
                    editorInstance.selection.collapse(false);
                    editorInstance.focus();
                }

                onClick?.(e);
            }}
            className={`absolute top-1/4 z-0 h-auto w-auto ${className}`}
            {...rest}
        />
    );
};

interface WysiwygSavedNotificationProps extends HTMLMotionProps<'div'> {
    show?: boolean;
}

const WysiwygSavedNotification = (props: WysiwygSavedNotificationProps) => {
    const { show = false, className = '', ...rest } = props;

    return (
        <AnimatePresence>
            {show && (
                <motion.div
                    className={`absolute right-8 top-8 flex items-center gap-2 ${className}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    {...rest}
                >
                    <FontAwesomeIcon size={'sm'} className={'text-green-500'} icon={faCheck} />
                    <p className={'text-sm font-medium text-green-500'}>Saved</p>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

type CustomPlugin = 'canva';

interface WysiwygContentEditorProps extends Omit<IAllProps, 'apiKey'> {
    disableCustomPlugins?: CustomPlugin[];
    autoResize?: boolean;
    sanitize?: (value: string) => string;
}

const WysiwygContentEditor = (props: WysiwygContentEditorProps) => {
    const { isBeta } = useCurrentAccount();
    const aiEnabled = isBeta;

    const {
        init = {} as IProps['init'],
        disableCustomPlugins = [],
        onInit,
        onBlur,
        onPaste,
        autoResize = true,
        sanitize,
        onEditorChange,
        ...rest
    } = props;

    const { settings } = useCurrentAccount();
    const themeColor = settings.find(item => item.code === Settings.ThemeColor.Code)?.value;
    const ctaBackgroundColor = themeColor ?? brandColors.cobalt.primary;
    const ctaTextColor = getMostReadableHexColor(ctaBackgroundColor);

    const {
        init_instance_callback,
        setup,
        placeholder = 'Write your content here...',
        inline = true,
        toolbar_persist = true,
        fixed_toolbar_container = `#${WysiwygConstants.TINY_TOOLBAR_ID}`,
        paste_data_images = true,
        powerpaste_word_import = 'clean',
        powerpaste_html_import = 'clean',
        powerpaste_googledocs_import = 'clean',
        powerpaste_allow_local_images = true,
        plugins = [
            'advlist',
            'autolink',
            'lists',
            'link',
            'image',
            'editimage',
            'charmap',
            'anchor',
            'searchreplace',
            'visualblocks',
            'insertdatetime',
            'help',
            'wordcount',
            'mergetags',
            'powerpaste',
            'emoticons',
            'table',
            'media',
            'pagebreak',
            ...(autoResize ? ['autoresize'] : []),
            ...(aiEnabled ? ['ai'] : []),
        ],
        font_family_formats = 'Arial=arial, helvetica, sans-serif; Helvetica=helvetica; Tahoma=tahoma,arial,helvetica,sans-serif; Times New Roman=times new roman, times;',
        font_size_formats = '8pt 10pt 12pt 14pt 16pt 18pt 24pt 36pt 48pt',
        formats = {
            calltoaction: {
                selector: 'a',
                styles: {
                    backgroundColor: ctaBackgroundColor,
                    color: ctaTextColor,
                    fontSize: '16px',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    display: 'inline-block',
                },
            },
        },
        style_formats = [
            {
                title: 'Headings',
                items: [
                    { title: 'Heading 1', format: 'h1' },
                    { title: 'Heading 2', format: 'h2' },
                    { title: 'Heading 3', format: 'h3' },
                    { title: 'Heading 4', format: 'h4' },
                    { title: 'Heading 5', format: 'h5' },
                    { title: 'Heading 6', format: 'h6' },
                ],
            },
            {
                title: 'Inline',
                items: [
                    { title: 'Bold', format: 'bold' },
                    { title: 'Italic', format: 'italic' },
                    { title: 'Underline', format: 'underline' },
                    { title: 'Strikethrough', format: 'strikethrough' },
                    { title: 'Superscript', format: 'superscript' },
                    { title: 'Subscript', format: 'subscript' },
                    { title: 'Code', format: 'code' },
                ],
            },
            {
                title: 'Blocks',
                items: [
                    { title: 'Paragraph', format: 'p' },
                    { title: 'Blockquote', format: 'blockquote' },
                    { title: 'Pre', format: 'pre' },
                ],
            },
            {
                title: 'Align',
                items: [
                    { title: 'Left', format: 'alignleft' },
                    { title: 'Center', format: 'aligncenter' },
                    { title: 'Right', format: 'alignright' },
                    { title: 'Justify', format: 'alignjustify' },
                ],
            },
        ],
        menu = {
            file: { title: 'File', items: 'newdocument pageSettings' },
            edit: {
                title: 'Edit',
                items: 'undo redo | cut copy paste pastetext | selectall | searchreplace',
            },
            view: {
                title: 'View',
                items: '',
            },
            insert: {
                title: 'Insert',
                items: 'qrCode image canva link inserttable | charmap emoticons hr | pagebreak nonbreaking tableofcontents | insertdatetime',
            },
            table: { title: '', items: '' },
            format: {
                title: 'Format',
                items: 'bold italic underline strikethrough superscript subscript | styles blocks fontfamily fontsize align lineheight | forecolor backcolor | language | removeformat',
            },
            tools: {
                title: 'Tools',
                items: 'spellchecker spellcheckerlanguage | a11ycheck code wordcount help',
            },
            help: {
                title: 'Help',
                items: '',
            },
        },
        toolbar = `
        aidialog aishortcuts |
        undo redo |
        bold italic underline strikethrough forecolor |
        mergetags qrCode emoticons |
        image canva link pagebreak |
        numlist bullist checklist |
        fontfamily fontsize blocks |
        alignleft aligncenter alignright alignjustify |
        outdent indent |
        casechange permanentpen formatpainter removeformat |
        `,
        toolbar_mode = 'wrap',
        help_tabs = ['shortcuts'],
        block_formats = 'Paragraph=p; Heading 1=h1; Heading 2=h2; Heading 3=h3; Heading 4=h4; Heading 5=h5; Heading 6=h6; Blockquote=blockquote;',
        content_style = 'body { font-family: Arial, Helvetica, sans-serif; }',
        pagebreak_separator = `<div style="page-break-before: always"></div>`,
        ...restInit
    } = init;
    const { setLastBookmark, setEditorInstance } = useContext(WysiwygContext);
    const { accountId } = useCurrentAccount();
    const { data: canvaApi } = useCanva();

    const { data: mergeTagsConfig } = useSuspenseQuery({
        queryKey: accountQueryKeys.engageConfiguration(accountId),
        queryFn: () => getEngageConfiguration(accountId),
        select: data => ({
            prefix: data.tags.prefix,
            suffix: data.tags.suffix,
            mergeTags: transformEngageTags(data.tags.items),
        }),
        staleTime: Infinity,
    });

    if (!import.meta.env.VITE_DASHBOARD_TINY_API_KEY) {
        return null;
    }

    const IMAGE_FAILED_ATTRIBUTE = 'data-image-failed';

    return (
        <TinyEditor
            apiKey={import.meta.env.VITE_DASHBOARD_TINY_API_KEY}
            onInit={(e, editor) => {
                setEditorInstance(editor);
                onInit?.(e, editor);
            }}
            onBlur={(e, editor) => {
                setLastBookmark(editor.selection.getBookmark(2));
                onBlur?.(e, editor);
            }}
            onPaste={(e, editor) => {
                editor.setDirty(true);
                onPaste?.(e, editor);
            }}
            onNodeChange={node => {
                if (node.element.nodeName.toLowerCase() === 'img') {
                    const el = node.element as HTMLImageElement;
                    if (el.width === 0 || el.height === 0 || !el.complete) {
                        el.width = el.width === 0 ? 100 : el.width;
                        el.height = el.height === 0 ? 100 : el.height;
                    }

                    el.onload = () => {
                        el.setAttribute(IMAGE_FAILED_ATTRIBUTE, 'false');
                    };
                    el.onerror = () => {
                        el.setAttribute(IMAGE_FAILED_ATTRIBUTE, 'true');
                    };
                }
            }}
            init={{
                placeholder,
                inline,
                toolbar_persist,
                fixed_toolbar_container,
                plugins,
                formats,
                font_family_formats,
                font_size_formats,
                style_formats,
                menu,
                toolbar,
                toolbar_mode,
                help_tabs,
                paste_data_images,
                powerpaste_word_import,
                powerpaste_googledocs_import,
                powerpaste_html_import,
                powerpaste_allow_local_images,
                block_formats,
                pagebreak_separator,
                mergetags_list: mergeTagsConfig?.mergeTags,
                mergetags_prefix: mergeTagsConfig?.prefix,
                mergetags_suffix: mergeTagsConfig?.suffix,
                content_style: `${content_style} img { max-width: 100%; height: auto; } img[${IMAGE_FAILED_ATTRIBUTE}="true"] { height: 100px; width: 100px; }`,
                contextmenu: false,
                mediaembed_inline_styles: true,
                editimage_toolbar: '',
                browser_spellcheck: true,
                init_instance_callback: editor => {
                    const helpMenuItem = editor.ui.registry.getAll().menuItems['help'];
                    if (helpMenuItem) helpMenuItem.text = 'Shortcuts';

                    const clearDocMenuItem = editor.ui.registry.getAll().menuItems['newdocument'];
                    if (clearDocMenuItem) clearDocMenuItem.text = 'Clear document';

                    init_instance_callback?.(editor);
                },
                setup: editor => {
                    if (!disableCustomPlugins.includes('canva')) {
                        if (!!canvaApi) {
                            editor.ui.registry.addIcon(
                                'canva',
                                '<svg width="20" height="20" viewBox="0 0 32 32" fill="rgba(0, 11, 29, 0.8)" xmlns="http://www.w3.org/2000/svg"> <path fill-rule="evenodd" clip-rule="evenodd" d="M15.3333 2C7.96954 2 2 7.96954 2 15.3333C2 22.6971 7.96954 28.6667 15.3333 28.6667C22.6971 28.6667 28.6667 22.6971 28.6667 15.3333C28.6667 7.96954 22.6971 2 15.3333 2ZM20.6068 17.6298C20.7095 17.7147 20.7813 17.8313 20.8107 17.9613C20.8365 18.0804 20.8372 18.2035 20.8129 18.3229C20.7886 18.4422 20.7397 18.5552 20.6694 18.6547L20.5307 18.88C20.4814 18.9627 20.4307 19.044 20.3787 19.1227C19.7494 20.084 19.0614 20.8067 18.272 21.332C17.5494 21.808 16.4214 22.4387 14.936 22.4453H14.9187C13.4454 22.4453 12.2307 21.5747 11.892 21.308C11.0307 20.6173 10.3614 19.5387 10.0574 18.3453C9.20536 15.0013 10.856 10.7987 13.736 8.97867C14.428 8.54565 15.2264 8.3126 16.0427 8.30534C17.212 8.30534 18.2627 8.83334 18.8054 9.68267C19.2454 10.3733 19.2147 11.452 18.7347 12.1387C18.28 12.7907 17.8614 12.9947 17.5214 12.8133C17.3925 12.7385 17.2952 12.6192 17.2479 12.4779C17.2005 12.3365 17.2063 12.1828 17.264 12.0453C17.6707 11.136 17.5547 10.652 17.1974 10.0773C17.0947 9.92477 16.9573 9.79877 16.7964 9.70975C16.6355 9.62073 16.4558 9.57123 16.272 9.56534C15.5907 9.568 14.788 10.1067 14.0694 11.0413C12.8027 12.6853 11.8294 15.5587 12.416 17.868C12.7494 19.172 13.7494 20.6947 15.4694 20.6947C16.0603 20.6853 16.6411 20.5398 17.1667 20.2693C18.336 19.6573 18.8294 19.22 19.456 18.2333C19.4934 18.1735 19.5299 18.113 19.5654 18.052L19.5758 18.0343C19.6207 17.9582 19.6681 17.8778 19.72 17.7987C19.7692 17.7033 19.8444 17.6239 19.9369 17.5696C20.0294 17.5153 20.1355 17.4884 20.2427 17.492C20.3759 17.4964 20.504 17.5448 20.6068 17.6298Z" fill="rgba(0, 11, 29, 0.8)" /></svg>',
                            );

                            const canvaAction = () => {
                                const canvaId = editor.selection
                                    .getNode()
                                    .getAttribute('data-canva-id');

                                const canvaProps: CanvaEditor & CanvaCallbacks = {
                                    editor: {
                                        fileType: 'jpg',
                                    },
                                    onDesignPublish: async opts => {
                                        // Canva may publish .zip or iframes; we only support images
                                        if (!isImage.exec(opts.exportUrl)) {
                                            const errorMsg = isZip.exec(opts.exportUrl)
                                                ? 'Please export one Canva page at a time'
                                                : 'This Canva file type is not supported. Please try again';
                                            editor.notificationManager.open({
                                                type: 'error',
                                                text: errorMsg,
                                            });
                                            return;
                                        }

                                        const { data } = await uploadRemoteMedia(
                                            accountId,
                                            opts.exportUrl,
                                        );

                                        const existingNode = editor.dom.select(
                                            `[data-canva-id="${canvaId}"]`,
                                        );

                                        if (existingNode) {
                                            editor.dom.remove(existingNode);
                                        }

                                        editor.insertContent(
                                            `<img height="200" data-canva-id="${opts.designId}" src="${data.url}" alt="Canva custom creation"/>`,
                                        );

                                        const images = editor.dom.select(
                                            `[data-canva-id="${opts.designId}"]`,
                                        );
                                        if (editor.selection && images.length > 0) {
                                            editor.selection.select(images[0]);
                                        }
                                    },
                                };

                                if (canvaId) {
                                    canvaApi.editDesign({
                                        design: {
                                            id: canvaId,
                                        },
                                        ...canvaProps,
                                    });
                                } else {
                                    canvaApi.createDesign({
                                        design: {
                                            type: 'Presentation43',
                                        },
                                        ...canvaProps,
                                    });
                                }
                            };

                            editor.ui.registry.addMenuItem('canva', {
                                icon: 'canva',
                                text: 'Canva',
                                onAction: canvaAction,
                            });

                            editor.ui.registry.addToggleButton('canva', {
                                icon: 'canva',
                                onAction: canvaAction,
                            });
                        }
                    }

                    editor.ui.registry.addContextToolbar('editQRCode', {
                        predicate: node =>
                            (node.nodeName.toLowerCase() === 'div' &&
                                node.id.includes('qr-code')) ||
                            (node.nodeName.toLowerCase() === 'img' && node.id.includes('qr-code')),
                        items: 'editQRCode',
                        position: 'node',
                        scope: 'node',
                    });

                    editor.ui.registry.addContextToolbar('editimage_toolbar', {
                        predicate: node =>
                            node.nodeName.toLowerCase() === 'img' && !node.id.includes('qr-code'),
                        items: 'rotateleft rotateright | flipv fliph | editimage imageoptions',
                        position: 'node',
                        scope: 'node',
                    });

                    setup?.(editor);
                },
                images_upload_handler: async blobInfo => {
                    if (blobInfo.filename().includes('.svg')) {
                        return Promise.resolve('data:image/svg+xml;base64,' + blobInfo.base64());
                    }
                    try {
                        const { data } = await uploadFile(blobInfo.blob(), blobInfo.filename());
                        return Promise.resolve(data.url);
                    } catch (error) {
                        const { response } = error as GivebutterAxiosError;
                        const message = response?.data?.message ?? 'Unable to upload file';
                        return Promise.reject(new TinyMceUploadError(message, true));
                    }
                },
                ai_request: aiRequestHandler(accountId),
                ...restInit,
            }}
            onEditorChange={(value, editor) => {
                const nextValue = sanitize ? sanitize(value) : value;
                onEditorChange?.(nextValue, editor);
            }}
            {...rest}
        />
    );
};

const Root = WysiwygProvider;
const Container = WysiwygContentContainer;
const Margins = WysiwygMargins;
const CursorManager = WysiwygCursorManager;
const Editor = WysiwygContentEditor;
const SavedNotification = WysiwygSavedNotification;
const CssReset = WysiwygCssReset;
const Toolbar = WysiwygToolbar;
const Constants = WysiwygConstants;

export {
    WysiwygProvider,
    WysiwygContentContainer,
    WysiwygMargins,
    WysiwygCursorManager,
    WysiwygContentEditor,
    WysiwygSavedNotification,
    WysiwygCssReset,
    WysiwygToolbar,
    WysiwygConstants,
    //
    Root,
    Container,
    Margins,
    CursorManager,
    Editor,
    SavedNotification,
    CssReset,
    Toolbar,
    Constants,
};
export type { WysiwygContentEditorProps };
